import React, { useContext, useEffect, useState } from 'react';
import Card from '../../../../../Common/Card';
import useTranslation from '../../../../../../util/hooks/useTranslation';
import Button from '../../../../../Common/Button';
import Table from '../../../../../Common/Table';
import useColumns from '../../../../../../util/hooks/useColumns';
import * as column from '../../../../../MainPages/util/grid/column';
import { SensorStatusStateContext } from '../../../index';
import ConfirmModal from '../../../../../Common/ConfirmModal';
import useAsync from '../../../../../../util/hooks/useAsync';
import { fetchExcelLogApi, fetchExcelSensorLogExport } from '../../../../../../api/log';
import useInterval from '../../../../../../util/hooks/useInterval';
import { INTERVAL_TIME } from '../../../sensorStatusReducer';
import moment from 'moment';
import { excelExport } from '../../../../../MainPages/Report/util/util';

// 센서 상세 정보
const SensorDetails = () => {
    const t = useTranslation('SensorStatus');
    const commonColumn = useTranslation('CommonColumn');

    const [modal, setModal] = useState({
        noData: false,
        excelResult: false,
    });
    const [excelInfo, setExcelInfo] = useState(null);

    const { sensorDetailsList, sensorDetailsListLoading, sensorLogInfo } = useContext(SensorStatusStateContext);

    const columns = useColumns([
        column.targetName({
            Header: 'Sensor Name',
        }),
        column.sensorMacAddress({
            width: 130,
        }),
        column.data({
            Cell: ({ value }) => {
                if (value) {
                    return <div>{value}</div>;
                } else {
                    return <div>-</div>;
                }
            },
        }),
        column.floor({
            width: 60,
            Cell: ({ value }) => {
                if (value) {
                    return <div>{value}</div>;
                } else {
                    return <div>-</div>;
                }
            },
        }),
        column.registeredDate({
            width: 100,
            Header: 'Recent Received Date',
            accessor: 'date',
            Cell: ({ value }) => {
                const date = moment.unix(value).format('YYYY-MM-DD HH:mm:ss');
                return <div>{date}</div>;
            },
        }),
    ]);

    useEffect(() => {
        // 엑셀 다운로드를 눌러서 엑셀 정보가 들어왔을 때 다운 받는 api 호출
        if (excelInfo) {
            toggleDelay();
        }
    }, [excelInfo]);

    const { promise: getExcelSensorLogExport } = useAsync({
        promise: fetchExcelSensorLogExport,
        fixedParam: {
            zipFileName: 'Sensor_Status_Log',
            fileName: 'Sensor_Status_Log',
            columnMetas: [
                {
                    key: 'targetName',
                    name: commonColumn('Target Name'),
                },
                {
                    key: 'deviceId',
                    name: commonColumn('Sensor Mac Address'),
                },
                {
                    key: 'data',
                    name: commonColumn('Value'),
                },
                {
                    key: 'floorId',
                    name: commonColumn('Floor'),
                },
                {
                    key: 'regDate',
                    name: commonColumn('Registered Date'),
                },
            ],
        },
        resolve: res => {
            const { filePath: url, reqNum } = res;

            if (url) {
                setExcelInfo({
                    url,
                    reqNum,
                });
            }
        },
    });

    const toggleDelay = useInterval({
        callback: param => {
            fetchExcelLog(param);
        },
        delay: INTERVAL_TIME,
        params: excelInfo ? { reqNum: excelInfo.reqNum } : {},
        initial: false,
    });

    const { promise: fetchExcelLog } = useAsync({
        promise: fetchExcelLogApi,
        resolve: res => {
            const { dataSettingStatusType, filePath: url, fileName } = res;
            if (dataSettingStatusType === 'COMPLETED') {
                setExcelInfo(null);
                toggleDelay();
                excelExport(url, fileName);
            } else if (dataSettingStatusType === 'FAILED') {
                setExcelInfo(null);
                toggleDelay();
                handleExcelDownloadResultModal();
            }
        },
    });

    const handleExportButtonClick = () => {
        const { request } = sensorLogInfo;

        if (!sensorDetailsList.rows.length || !request) {
            handleNoDataModal();
        } else {
            delete request.page;
            delete request.pageSize;

            getExcelSensorLogExport({ ...request });
        }
    };

    const handleNoDataModal = () => {
        setModal(prev => {
            return {
                ...prev,
                noData: !prev.noData,
            };
        });
    };

    const handleExcelDownloadResultModal = () => {
        setModal(prev => {
            return {
                ...prev,
                excelResult: !prev.excelResult,
            };
        });
    };

    return (
        <>
            <Card
                header={{
                    title: t('Sensor details'),
                    action: (
                        <Button className={'btn-secondary'} onClick={handleExportButtonClick}>
                            {t('Export', 'Button')}
                        </Button>
                    ),
                }}
            >
                <Table paging={false} columns={columns} data={sensorDetailsList} loading={sensorDetailsListLoading} />
            </Card>

            <ConfirmModal
                initModal={modal.noData}
                toggleModal={handleNoDataModal}
                header={{ title: t('Alert') }}
                confirmText={<span>{t('There is no data')}</span>}
                removeCancel={true}
            />

            <ConfirmModal
                initModal={modal.excelResult}
                toggleModal={handleExcelDownloadResultModal}
                header={{ title: t('Result', 'ConfirmModal') }}
                confirmText={<span>{t('Failed to download the excel.', 'ConfirmModal')}</span>}
                removeCancel={true}
            />
        </>
    );
};

export default SensorDetails;
